/**
 * Email di sistema per-tenant (setting master) — tab in TenantDetail.
 * Vista per scope + dialog edit (password write-only) + test invio reale.
 */
import { useCallback, useEffect, useState } from 'react';
import apiClient from '@/lib/api';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { Label } from '@easyflow/ui-master/components/ui/label';
import { Switch } from '@easyflow/ui-master/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@easyflow/ui-master/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@easyflow/ui-master/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@easyflow/ui-master/components/ui/table';
import { Loader2, Mail, Pencil, Plus, Send, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

type SystemEmailScope =
  | 'default' | 'transactional' | 'notifications' | 'support' | 'billing'
  | 'email_verification' | 'master_users' | 'crm_master' | 'stripe';

const SCOPE_LABELS: Record<SystemEmailScope, string> = {
  default: 'Default (fallback tenant)',
  transactional: 'Transazionali (reset, inviti)',
  notifications: 'Notifiche applicative',
  support: 'Support / ticket',
  billing: 'Fatturazione',
  email_verification: 'Verifica email utenti (piattaforma)',
  master_users: 'Utenti Master (reset/inviti)',
  crm_master: 'CRM Master (preventivi, contratti, OTP)',
  stripe: 'Stripe (rate e addebiti)',
};

const ALL_SCOPES: SystemEmailScope[] = [
  'default', 'transactional', 'notifications', 'support', 'billing',
  'email_verification', 'master_users', 'crm_master', 'stripe',
];

interface SystemEmailRow {
  id: string;
  scope: SystemEmailScope;
  from_name: string;
  from_address: string;
  reply_to: string | null;
  description: string | null;
  smtp_host: string;
  smtp_port: number;
  smtp_security: 'ssl' | 'starttls' | 'none';
  smtp_user: string;
  is_active: boolean;
  last_test_at: string | null;
  last_test_result: 'success' | 'failure' | null;
  last_test_error: string | null;
}

interface FormState {
  scope: SystemEmailScope;
  from_name: string;
  from_address: string;
  reply_to: string;
  description: string;
  smtp_host: string;
  smtp_port: string;
  smtp_security: 'ssl' | 'starttls' | 'none';
  smtp_user: string;
  smtp_password: string;
  is_active: boolean;
}

const EMPTY_FORM: FormState = {
  scope: 'default', from_name: '', from_address: '', reply_to: '', description: '',
  smtp_host: '', smtp_port: '465', smtp_security: 'ssl', smtp_user: '', smtp_password: '',
  is_active: true,
};

interface ApiEnvelope<T> { data?: T; }

export default function TenantSystemEmails({ tenantId }: { tenantId: string | undefined }) {
  const { toast } = useToast();
  const [rows, setRows] = useState<SystemEmailRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingScope, setEditingScope] = useState<SystemEmailScope | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [testDialogScope, setTestDialogScope] = useState<SystemEmailScope | null>(null);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const resp = await apiClient.get<ApiEnvelope<SystemEmailRow[]> | SystemEmailRow[]>(`/master/system-emails/${tenantId}`);
      const payload = resp.data;
      const list = Array.isArray(payload) ? payload : (payload?.data ?? []);
      setRows(list);
    } catch {
      toast({ title: 'Errore', description: 'Impossibile caricare le email di sistema', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { void load(); }, [load]);

  const openCreate = () => {
    const used = new Set(rows.map(r => r.scope));
    const firstFree = ALL_SCOPES.find(s => !used.has(s)) ?? 'default';
    setEditingScope(null);
    setForm({ ...EMPTY_FORM, scope: firstFree });
    setDialogOpen(true);
  };

  const openEdit = (row: SystemEmailRow) => {
    setEditingScope(row.scope);
    setForm({
      scope: row.scope,
      from_name: row.from_name,
      from_address: row.from_address,
      reply_to: row.reply_to ?? '',
      description: row.description ?? '',
      smtp_host: row.smtp_host,
      smtp_port: String(row.smtp_port),
      smtp_security: row.smtp_security,
      smtp_user: row.smtp_user,
      smtp_password: '',
      is_active: row.is_active,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!tenantId) return;
    const port = parseInt(form.smtp_port, 10);
    if (!form.from_name || !form.from_address || !form.smtp_host || !form.smtp_user || Number.isNaN(port)) {
      toast({ title: 'Campi mancanti', description: 'Compila tutti i campi obbligatori', variant: 'destructive' });
      return;
    }
    if (editingScope === null && !form.smtp_password) {
      toast({ title: 'Password mancante', description: 'La password SMTP è obbligatoria per una nuova casella', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await apiClient.put(`/master/system-emails/${tenantId}`, {
        scope: form.scope,
        from_name: form.from_name,
        from_address: form.from_address,
        reply_to: form.reply_to || null,
        description: form.description || null,
        smtp_host: form.smtp_host,
        smtp_port: port,
        smtp_security: form.smtp_security,
        smtp_user: form.smtp_user,
        ...(form.smtp_password ? { smtp_password: form.smtp_password } : {}),
        is_active: form.is_active,
      });
      toast({ title: 'Casella salvata' });
      setDialogOpen(false);
      void load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Salvataggio fallito';
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (scope: SystemEmailScope) => {
    if (!tenantId) return;
    if (!window.confirm(`Rimuovere la casella per lo scope "${SCOPE_LABELS[scope]}"? Le email torneranno alla casella di piattaforma.`)) return;
    try {
      await apiClient.delete(`/master/system-emails/${tenantId}/${scope}`);
      toast({ title: 'Casella rimossa' });
      void load();
    } catch {
      toast({ title: 'Errore', description: 'Rimozione fallita', variant: 'destructive' });
    }
  };

  const runTest = async () => {
    if (!tenantId || !testDialogScope || !testTo) return;
    setTesting(true);
    try {
      const resp = await apiClient.post<ApiEnvelope<{ ok: boolean; error?: string }> | { ok: boolean; error?: string }>(
        `/master/system-emails/${tenantId}/test`,
        { scope: testDialogScope, to: testTo }
      );
      const payload = resp.data;
      const outcome = 'ok' in (payload as { ok?: boolean }) ? (payload as { ok: boolean; error?: string }) : ((payload as ApiEnvelope<{ ok: boolean; error?: string }>).data ?? { ok: false });
      if (outcome.ok) toast({ title: 'Test inviato', description: `Email di test inviata a ${testTo}` });
      else toast({ title: 'Test fallito', description: outcome.error ?? 'errore SMTP', variant: 'destructive' });
      setTestDialogScope(null);
      setTestTo('');
      void load();
    } catch {
      toast({ title: 'Errore', description: 'Test fallito', variant: 'destructive' });
    } finally {
      setTesting(false);
    }
  };

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
        <span className="ml-2 text-muted-foreground">Caricamento email di sistema...</span>
      </div>
    );
  }

  const usedScopes = new Set(rows.map(r => r.scope));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Mail className="h-4 w-4" /> Caselle email di sistema
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Mittenti dedicati per le email di sistema di questo tenant, per ambito.
            In assenza di casella per uno scope si usa la "Default" del tenant, altrimenti la casella di piattaforma.
            Assicurati che SPF/DKIM del dominio mittente autorizzino il server SMTP indicato.
          </p>
        </div>
        <Button size="sm" onClick={openCreate} disabled={usedScopes.size >= ALL_SCOPES.length}>
          <Plus className="h-4 w-4 mr-1" /> Nuova casella
        </Button>
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground text-sm border rounded-lg">
          Nessuna casella configurata: tutte le email di sistema usano la casella di piattaforma.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Scope</TableHead>
              <TableHead>Mittente</TableHead>
              <TableHead>SMTP</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead>Ultimo test</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.id}>
                <TableCell>
                  <div className="font-medium text-sm">{SCOPE_LABELS[row.scope]}</div>
                  {row.description && <div className="text-xs text-muted-foreground">{row.description}</div>}
                </TableCell>
                <TableCell>
                  <div className="text-sm">{row.from_name}</div>
                  <div className="text-xs text-muted-foreground">{row.from_address}</div>
                </TableCell>
                <TableCell className="text-xs">
                  {row.smtp_host}:{row.smtp_port} ({row.smtp_security})
                </TableCell>
                <TableCell>
                  <Badge variant={row.is_active ? 'default' : 'secondary'}>
                    {row.is_active ? 'Attiva' : 'Disattivata'}
                  </Badge>
                </TableCell>
                <TableCell>
                  {row.last_test_at ? (
                    <div className="text-xs">
                      <Badge variant={row.last_test_result === 'success' ? 'default' : 'destructive'}>
                        {row.last_test_result === 'success' ? 'OK' : 'KO'}
                      </Badge>
                      <div className="text-muted-foreground mt-0.5">
                        {new Date(row.last_test_at).toLocaleString('it-IT')}
                      </div>
                      {row.last_test_result === 'failure' && row.last_test_error && (
                        <div className="text-destructive truncate max-w-[220px]" title={row.last_test_error}>
                          {row.last_test_error}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">Mai testata</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="sm" title="Invia email di test"
                      onClick={() => { setTestDialogScope(row.scope); setTestTo(''); }}>
                      <Send className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" title="Modifica" onClick={() => openEdit(row)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" title="Rimuovi" onClick={() => void remove(row.scope)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>{editingScope ? 'Modifica casella' : 'Nuova casella di sistema'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Scope</Label>
              <Select value={form.scope} onValueChange={(v) => set('scope', v as SystemEmailScope)} disabled={editingScope !== null}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ALL_SCOPES.map(s => (
                    <SelectItem key={s} value={s} disabled={editingScope === null && usedScopes.has(s)}>
                      {SCOPE_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nome mittente *</Label>
              <Input value={form.from_name} onChange={e => set('from_name', e.target.value)} placeholder="Es. Acme Notifiche" />
            </div>
            <div>
              <Label>Indirizzo mittente *</Label>
              <Input value={form.from_address} onChange={e => set('from_address', e.target.value)} placeholder="noreply@acme.it" />
            </div>
            <div>
              <Label>Reply-To</Label>
              <Input value={form.reply_to} onChange={e => set('reply_to', e.target.value)} placeholder="support@acme.it (opzionale)" />
            </div>
            <div>
              <Label>Descrizione</Label>
              <Input value={form.description} onChange={e => set('description', e.target.value)} placeholder="Per cosa si usa (opzionale)" />
            </div>
            <div>
              <Label>Host SMTP *</Label>
              <Input value={form.smtp_host} onChange={e => set('smtp_host', e.target.value)} placeholder="smtp.acme.it" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Porta *</Label>
                <Input value={form.smtp_port} onChange={e => set('smtp_port', e.target.value)} inputMode="numeric" />
              </div>
              <div>
                <Label>Sicurezza</Label>
                <Select value={form.smtp_security} onValueChange={(v) => set('smtp_security', v as FormState['smtp_security'])}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ssl">SSL (465)</SelectItem>
                    <SelectItem value="starttls">STARTTLS (587)</SelectItem>
                    <SelectItem value="none">Nessuna</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Utente SMTP *</Label>
              <Input value={form.smtp_user} onChange={e => set('smtp_user', e.target.value)} placeholder="noreply@acme.it" />
            </div>
            <div>
              <Label>{editingScope ? 'Password SMTP (vuota = invariata)' : 'Password SMTP *'}</Label>
              <Input type="password" value={form.smtp_password} onChange={e => set('smtp_password', e.target.value)} autoComplete="new-password" />
            </div>
            <div className="col-span-2 flex items-center gap-2 pt-1">
              <Switch checked={form.is_active} onCheckedChange={(v) => set('is_active', v)} />
              <Label>Casella attiva</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={() => void save()} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={testDialogScope !== null} onOpenChange={(open) => { if (!open) setTestDialogScope(null); }}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Invia email di test</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Destinatario</Label>
            <Input value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="tuo@indirizzo.it" />
            <p className="text-xs text-muted-foreground">
              Verifica connessione e credenziali SMTP e invia una email reale dalla casella
              {testDialogScope ? ` "${SCOPE_LABELS[testDialogScope]}"` : ''}. L'esito viene salvato.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestDialogScope(null)}>Annulla</Button>
            <Button onClick={() => void runTest()} disabled={testing || !testTo}>
              {testing && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Invia test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
